/**
 * Utility functions for serializing and deserializing Templates page
 * search/filter state to/from URL query parameters.
 *
 * Query param names:
 *   search      – free-text search string
 *   productType – repeated param for each selected product title
 *                 e.g. ?productType=Postcards+-+4x6&productType=Real+Penned+Letter
 *   createdBy   – creator name filter
 *   sortBy / sortOrder – column sort (see TEMPLATE_SORT_KEYS)
 */

import { buildSortQueryParams, parseSortSearchParams } from '~/utils/sort-params';

// Must match the API allowlist for GET /templates.
export const TEMPLATE_SORT_KEYS = ['name', 'productType', 'updatedAt', 'createdBy', 'createdAt'];

/**
 * Parse URL search params into the shape used by the Templates page.
 *
 * @param {URLSearchParams} searchParams
 * @returns {{ search: string, searchProductIds: string, searchCreator: string, sort: { sortBy?: string, sortOrder?: string } }}
 */
export function parseTemplateQueryParams(searchParams) {
  return {
    search: searchParams.get('search') || '',
    // productType is a repeated param – join titles with comma to match
    // the existing Redux field `searchProductIds` format.
    searchProductIds: searchParams.getAll('productType').join(','),
    searchCreator: searchParams.get('createdBy') || '',
    sort: parseSortSearchParams(searchParams, TEMPLATE_SORT_KEYS),
  };
}

/**
 * Build a plain object suitable for passing to React Router's setSearchParams().
 * Empty values are omitted so the URL stays clean.
 *
 * @param {{ search: string, searchProductIds: string, searchCreator: string, sort?: { sortBy?: string, sortOrder?: string } }} state
 * @returns {Record<string, string | string[]>}
 */
export function buildTemplateQueryParams({ search, searchProductIds, searchCreator, sort }) {
  const params = {};

  if (search && search.trim()) {
    params.search = search.trim();
  }

  if (searchProductIds && searchProductIds.trim()) {
    const titles = searchProductIds.split(',').map((t) => t.trim()).filter(Boolean);
    if (titles.length > 0) {
      params.productType = titles;
    }
  }

  if (searchCreator && searchCreator.trim()) {
    params.createdBy = searchCreator.trim();
  }

  return { ...params, ...buildSortQueryParams(sort) };
}
